import { AttributeValueInput } from "../types";
import { MappingContext } from "../orchestrator";
import { aiMedicationMapperConfigSchema } from "../schemas";
import { withScope, captureException } from "@sentry/react";
import { memoizeMapperFactory } from "../utils/memoize";

/**
 * Internal implementation of aiMedicationMapper without memoization.
 * This mapper processes AI-recognized medications and maps them to attributes
 * in a way that's compatible with the existing medication grid format.
 */
function aiMedicationMapperImpl(config: unknown) {
  const parsedConfig = aiMedicationMapperConfigSchema.parse(config);
  const { categoryAttributeMap } = parsedConfig;

  return (response: string, _context: MappingContext): AttributeValueInput[] => {
    const attributes: AttributeValueInput[] = [];

    if (typeof response !== "string") {
      return [];
    }

    try {
      // Parse the JSON string from the AI medication entry
      const parsedData = JSON.parse(response);
      
      // Handle the nested structure from AI medication entry
      const medicationData = parsedData.medications || parsedData;
      
      // Process each medication entry
      Object.entries(medicationData).forEach(([, statusValue]) => {
        if (typeof statusValue !== "string") return;
        
        // Extract medication key and status from the value (e.g., "semaglutide_current")
        const parts = statusValue.split("_");
        if (parts.length < 2) return;
        
        const medicationKey = parts[0];
        const status = parts.slice(1).join("_");
        
        // Find the appropriate attribute ID
        let attributeId: number | undefined;
        
        // Search through categories to find the medication
        for (const [, mappings] of Object.entries(categoryAttributeMap)) {
          if (typeof mappings === "object" && "all" in mappings) {
            // Category maps all medications to a single attribute
            attributeId = mappings.all;
            break;
          } else if (medicationKey in mappings) {
            // Found specific medication mapping
            attributeId = mappings[medicationKey];
            break;
          }
        }
        
        if (!attributeId) {
          console.warn(`No attribute mapping found for medication: ${medicationKey}`);
          return;
        }
        
        // Skip "not_taking" values to match existing behavior
        if (status === "not_taking") {
          return;
        }
        
        const hasTaken = status !== "never";
        
        // Create medication attribute value
        attributes.push({
          attributeDefinitionId: attributeId,
          medicationAttributeValue: {
            hasTaken,
            intervals: hasTaken ? createMedicationInterval(status) : undefined,
          },
          isSkipped: false,
        });
      });
    } catch (e) {
      console.error("Failed to parse AI medication response:", response, e);
      
      withScope((scope) => {
        scope.setTag("location", "aiMedicationMapper");
        scope.setContext("mapperContext", {
          response,
          categoryAttributeMap,
        });
        captureException(e);
      });
    }

    return attributes;
  };
}

/**
 * Creates medication interval based on status type
 */
function createMedicationInterval(statusType: string) {
  if (statusType === "current") {
    return [{ start: {}, end: { current: true } }];
  }
  // Handle all past statuses
  if (statusType.startsWith("stopped_") || statusType === "past") {
    // When isBeforeX=true, the API requires only year to be set
    // Default to 2024 as most questions are about stopping long ago
    return [{ start: {}, end: { isBeforeX: true, year: 2024 } }];
  }
  return undefined;
}

/**
 * Creates a mapper for AI-powered medication entry.
 * It parses the AI-generated medication data and maps it to medication attributes.
 * This function is memoized to avoid repeated config parsing.
 */
export const aiMedicationMapper = memoizeMapperFactory(aiMedicationMapperImpl);